import { memo, ReactElement, ReactNode } from 'react';

import { isAfter, isSameDay, startOfDay } from 'date-fns';

import { parseDate } from '@utils/date';

import {
  Condition,
  ConditionComparator,
  FieldWithCondition,
  FormFieldCondition,
  FormFieldValue,
  isFormSourceCondition,
  isNegativeComparator,
} from './types';

import { useFormContext } from '../form-provider';
import { FormStateValues } from '../types';

export type FormConditionalProps = {
  field: FieldWithCondition;
  children: ReactNode | ((isVisible: boolean) => ReactElement);
};

export const ConditionalDisplay = memo<FormConditionalProps>(({ children, field }) => {
  const {
    store: { useWatchValues },
  } = useFormContext();

  const values = useWatchValues(findQuestionIds(field.condition));
  const isVisible = shouldDisplay(field.condition, values);

  if (typeof children === 'function') {
    return children(isVisible);
  }

  if (!isVisible) {
    return null;
  }

  return children;
});

export const matchesCondition = (condition: FormFieldCondition, value: any): boolean => {
  if (isFormSourceCondition(condition)) {
    return compareValues(value, condition.comparator, condition.value);
  }
  return compareValues(condition.resolvedValue ?? null, condition.comparator, condition.value);
};

// Journey-resolved date values arrive as ISO strings (FormFieldValue cannot
// be a Date), while form state holds real Date instances. Accept both.
const coerceDate = (value: unknown): Date | null => {
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return null;
  const parsed = parseDate(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const compareValues = (
  leftValue: any,
  comparator: ConditionComparator,
  rightValue: FormFieldValue
): boolean => {
  // Nullish leftValue is "absent"; 0/false/'' must reach the switch below.
  if (leftValue == null) {
    return isNegativeComparator(comparator);
  }

  switch (comparator) {
    case 'after': {
      if (typeof rightValue !== 'string') return false;
      const left = coerceDate(leftValue);
      if (!left) return false;
      return isAfter(startOfDay(left), startOfDay(parseDate(rightValue)));
    }

    case 'on': {
      if (typeof rightValue !== 'string') return false;
      const left = coerceDate(leftValue);
      if (!left) return false;
      return isSameDay(left, parseDate(rightValue));
    }

    case 'before': {
      if (typeof rightValue !== 'string') return false;
      const left = coerceDate(leftValue);
      if (!left) return false;
      return isAfter(startOfDay(parseDate(rightValue)), startOfDay(left));
    }

    case 'any_of': {
      if (!Array.isArray(rightValue)) return false;

      if (Array.isArray(leftValue)) {
        return leftValue.some((entry) => rightValue.includes(entry));
      }

      return rightValue.includes(leftValue);
    }

    case 'none_of': {
      if (!Array.isArray(rightValue)) return false;

      if (Array.isArray(leftValue)) {
        return !leftValue.some((entry) => rightValue.includes(entry));
      }

      return !rightValue.includes(leftValue);
    }

    case 'is': {
      if (Array.isArray(leftValue)) {
        return leftValue.includes(rightValue);
      }
      return leftValue === rightValue;
    }

    case 'not_is': {
      if (Array.isArray(leftValue)) {
        return !leftValue.includes(rightValue);
      }
      return leftValue !== rightValue;
    }

    case 'starts': {
      if (typeof leftValue !== 'string' || typeof rightValue !== 'string') return false;
      return leftValue.toLowerCase().startsWith(rightValue.toLowerCase());
    }

    case 'not_starts': {
      if (typeof leftValue !== 'string' || typeof rightValue !== 'string') return true;
      return !leftValue.toLowerCase().startsWith(rightValue.toLowerCase());
    }

    case 'contains': {
      if (typeof leftValue !== 'string' || typeof rightValue !== 'string') return false;
      return leftValue.toLowerCase().includes(rightValue.toLowerCase());
    }

    case 'not_contains': {
      if (typeof leftValue !== 'string' || typeof rightValue !== 'string') return true;
      return !leftValue.toLowerCase().includes(rightValue.toLowerCase());
    }

    default:
      return false;
  }
};

const findQuestionIds = (condition: Condition): Array<string> =>
  condition.conditions.flatMap((entry) => (isFormSourceCondition(entry) ? [entry.questionId] : []));

const getValueForCondition = (
  condition: FormFieldCondition,
  state: FormStateValues
): FormStateValues[string] | null => {
  if (!isFormSourceCondition(condition)) return null;
  return state[condition.questionId];
};

// Exported for testing; ConditionalDisplay is the public surface.
export const shouldDisplay = (condition: Condition, state: FormStateValues = {}): boolean => {
  if (condition.conditions.length === 0) return true;

  switch (condition.op) {
    case 'AND':
      return condition.conditions.every((entry) =>
        matchesCondition(entry, getValueForCondition(entry, state))
      );
    case 'OR':
      return condition.conditions.some((entry) =>
        matchesCondition(entry, getValueForCondition(entry, state))
      );
    default:
      return true;
  }
};
